import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Popconfirm, Table, Button, Tooltip } from 'antd';
import { Pencil, Trash2, Building2, UserRound, MapPin, Download, Store } from 'lucide-react';
import TableActions from './TableActions';
import { TagsDisplay } from '../../tags/elements';
import { User } from '../../../data';
import { SectionCard, StatusTag, IconAction, IconActions, EmptyState } from '../../common';
import { useStoreTags, useStoreTableState, filterAndSortRows, exportStoresToExcel } from './storeTable.controller';
import { t } from '../../../company/labels';

const plural = (n) => (n > 1 ? 's' : '');

/** Promotor names as compact pills (first two + "+N" with the full list in a tooltip). */
function PromotorPills({ text }) {
    if (!text || text.trim() === '') {
        return (
            <span className="inline-flex items-center gap-1.5 text-xs text-surface-400">
                <UserRound />
                Sin asignar
            </span>
        );
    }

    const promotorList = text.split(', ');
    const pills = (list) =>
        list.map((promotor, idx) => (
            <StatusTag key={idx} tone="brand">
                {promotor}
            </StatusTag>
        ));

    if (promotorList.length > 2) {
        return (
            <Tooltip title={text}>
                <div className="flex flex-wrap gap-1">
                    {pills(promotorList.slice(0, 2))}
                    <StatusTag tone="neutral">+{promotorList.length - 2}</StatusTag>
                </div>
            </Tooltip>
        );
    }

    return <div className="flex flex-wrap gap-1">{pills(promotorList)}</div>;
}

export default function StoreTable({ stores, rows, loading, setRows, showDrawer, deactivateStore, selectedRowKeys, setSelectedRowKeys, onBulkDeactivate }) {
    const navigate = useNavigate();
    const isClientUser = User.getInstance()?.userType === 'client';
    const { availableTags, storeTagsMap } = useStoreTags(stores);
    const { columnFilters, sortedInfo, onTableChange } = useStoreTableState();

    const StoreColumns = [
        {
            title: 'Piso de Venta',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            width: 240,
            render: (text, record) => (
                <div className="flex flex-col min-w-0">
                    <button
                        type="button"
                        onClick={() => navigate(`/dashboard/stores/store?id=${record._id}`)}
                        className="p-0 bg-transparent border-0 cursor-pointer text-left text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline">
                        {text}
                    </button>
                    <span className="text-xs text-surface-400 tabular-nums">RIF: {record.rif}</span>
                </div>
            ),
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null
        },
        {
            title: 'Cliente',
            dataIndex: 'clientName',
            key: 'clientName',
            width: 190,
            render: (text) => (
                <span className="inline-flex items-center gap-2 text-sm text-surface-700">
                    <Building2 className="text-surface-400" />
                    {text || '--'}
                </span>
            ),
            sorter: (a, b) => (a.clientName || '').localeCompare(b.clientName || ''),
            sortOrder: sortedInfo.columnKey === 'clientName' ? sortedInfo.order : null
        },
        {
            title: 'Canal',
            dataIndex: 'channel',
            key: 'channel',
            width: 130,
            render: (text) => (
                <StatusTag tone={text === 'MODERNO' ? 'info' : 'warning'} dot className="capitalize">
                    {text?.toLowerCase()}
                </StatusTag>
            ),
            filters: [
                { text: 'Moderno', value: 'MODERNO' },
                { text: 'Tradicional', value: 'TRADICIONAL' }
            ],
            filteredValue: columnFilters.channel || null,
            onFilter: (value, record) => record.channel === value
        },
        {
            title: 'Tipo de Piso',
            dataIndex: 'storeType',
            key: 'storeType',
            width: 140,
            render: (text) => <span className="text-sm text-surface-600 capitalize">{text || 'Sin tipo'}</span>,
            sorter: (a, b) => (a.storeType || '').localeCompare(b.storeType || ''),
            sortOrder: sortedInfo.columnKey === 'storeType' ? sortedInfo.order : null
        },
        {
            title: t('promotores'),
            dataIndex: 'promotorNames',
            key: 'promotorNames',
            width: 220,
            render: (text) => <PromotorPills text={text} />
        },
        {
            title: 'Coordenadas',
            dataIndex: 'coordinates',
            key: 'coordinates',
            width: 130,
            render: (coords) => {
                if (coords?.latitude && coords?.longitude) {
                    return (
                        <Tooltip title={`${coords.latitude}, ${coords.longitude}`}>
                            <span>
                                <StatusTag tone="success" icon={<MapPin />}>
                                    GPS
                                </StatusTag>
                            </span>
                        </Tooltip>
                    );
                }
                return (
                    <StatusTag tone="neutral" icon={<MapPin />}>
                        Sin GPS
                    </StatusTag>
                );
            },
            filters: [
                { text: 'Con GPS', value: 'with' },
                { text: 'Sin GPS', value: 'without' }
            ],
            filteredValue: columnFilters.coordinates || null,
            onFilter: (value, record) => {
                const hasCoords = record.coordinates?.latitude && record.coordinates?.longitude;
                return value === 'with' ? hasCoords : !hasCoords;
            }
        },
        {
            title: 'Estado',
            dataIndex: 'active',
            key: 'active',
            width: 110,
            render: (active) => (
                <StatusTag tone={active === 'activo' ? 'success' : 'danger'} dot className="capitalize">
                    {active}
                </StatusTag>
            ),
            filters: [
                { text: 'Activo', value: 'activo' },
                { text: 'Inactivo', value: 'inactivo' }
            ],
            filteredValue: columnFilters.active || null,
            onFilter: (value, record) => record.active === value
        },
        {
            title: 'Etiquetas',
            dataIndex: '_id',
            key: 'tags',
            width: 200,
            render: (id) => <TagsDisplay modelType="Store" modelId={id} editable={true} />,
            filters: availableTags.map((tag) => ({
                text: tag.name,
                value: tag._id
            })),
            filterMultiple: true,
            filteredValue: columnFilters.tags || null,
            onFilter: (value, record) => {
                const storeTags = storeTagsMap[record._id] || [];
                return storeTags.some((tag) => tag._id === value);
            }
        },
        ...(!isClientUser
            ? [
                  {
                      title: 'Acciones',
                      dataIndex: '_id',
                      key: '_id',
                      fixed: 'right',
                      width: 96,
                      align: 'right',
                      render: (text) => (
                          <IconActions>
                              <IconAction icon={<Pencil />} label="Editar PDV" onClick={async () => await showDrawer(text)} />
                              <IconAction
                                  icon={<Trash2 />}
                                  label="Borrar PDV"
                                  danger
                                  confirm={{
                                      title: 'Borrar PDV',
                                      description: '¿Estás seguro que deseas eliminar este PDV?',
                                      okText: 'Sí',
                                      cancelText: 'No'
                                  }}
                                  onClick={async () => await deactivateStore(text)}
                              />
                          </IconActions>
                      )
                  }
              ]
            : [])
    ];

    const visibleRows = useMemo(
        () => filterAndSortRows(rows, StoreColumns, columnFilters, sortedInfo),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, columnFilters, sortedInfo, storeTagsMap]
    );

    const exportToExcel = () => exportStoresToExcel(visibleRows, storeTagsMap);

    const rowSelection = isClientUser
        ? undefined
        : {
              selectedRowKeys: selectedRowKeys || [],
              onChange: setSelectedRowKeys,
              getCheckboxProps: (record) => ({ disabled: record.active === 'inactivo' })
          };

    const selectedCount = selectedRowKeys?.length || 0;

    return (
        <div>
            <TableActions setRows={setRows} storesData={stores} />

            <SectionCard
                flush
                title="Listado de pisos de venta"
                description={`${visibleRows.length} de ${stores?.length || 0} pisos de venta`}
                extra={
                    // Export — exports exactly what the table is showing
                    <Button icon={<Download />} onClick={exportToExcel} disabled={!visibleRows.length}>
                        Exportar a Excel{visibleRows.length ? ` (${visibleRows.length})` : ''}
                    </Button>
                }>
                {/* Bulk action bar — visible only when rows are selected */}
                {!isClientUser && selectedCount > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-2.5 bg-danger-bg border-b border-danger-border">
                        <div className="flex items-center gap-3 text-sm">
                            <Trash2 className="text-danger-fg" />
                            <span className="font-medium text-danger-fg tabular-nums">
                                {selectedCount} PDV{plural(selectedCount)} seleccionado{plural(selectedCount)}
                            </span>
                            <Button type="link" size="small" onClick={() => setSelectedRowKeys([])}>
                                Deseleccionar todo
                            </Button>
                        </div>
                        <Popconfirm
                            title={`Eliminar ${selectedCount} PDV${plural(selectedCount)}`}
                            description={`Se desactivarán y serán removidos de todos los calendarios de ${t('promotoresLower')}. ¿Continuar?`}
                            onConfirm={onBulkDeactivate}
                            okText="Sí, eliminar"
                            cancelText="Cancelar"
                            okButtonProps={{ danger: true }}>
                            <Button danger icon={<Trash2 />}>
                                Eliminar {selectedCount} PDV{plural(selectedCount)}
                            </Button>
                        </Popconfirm>
                    </div>
                )}

                <Table
                    size="middle"
                    loading={loading}
                    dataSource={rows}
                    columns={StoreColumns}
                    rowSelection={rowSelection}
                    onChange={onTableChange}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} pisos de venta`
                    }}
                    scroll={{ x: 1400 }}
                    rowKey="_id"
                    className="[&_.ant-pagination]:px-5"
                    locale={{
                        emptyText: <EmptyState icon={<Store />} title="Sin pisos de venta" description="No hay pisos de venta que coincidan con los filtros." />
                    }}
                />
            </SectionCard>
        </div>
    );
}
